// Visualização da hierarquia: team supervisor + members + tools + prompt.
// Drill-down: clica num member abre painel lateral com prompt + tools + model.

import { useEffect, useState } from 'preact/hooks';
import { getTeam, listAgents, listTeams } from '../lib/api';
import type { AgentSummary, TeamSummary, ToolSummary } from '../types/agno-api';
import { active, activeToken } from '../store/global';
import { TeamFlow } from './team-map/TeamFlow';
import { normalizeTools } from './team-map/layout';

type Node = TeamSummary | AgentSummary;

export function TeamMap() {
  const [team, setTeam] = useState<TeamSummary | null>(null);
  const [agents, setAgents] = useState<AgentSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<Node | null>(null);

  useEffect(() => {
    if (!activeToken.value) return;
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const allAgents = (await listAgents().catch(() => [])) as AgentSummary[];
        if (!alive) return;
        setAgents(allAgents);

        const ref = active.value;
        if (ref?.isTeam && ref.targetId) {
          const t = await getTeam(ref.targetId).catch(() => null);
          if (!alive) return;
          setTeam(t);
        } else {
          const allTeams = (await listTeams().catch(() => [])) as TeamSummary[];
          if (allTeams[0]) {
            const t = await getTeam(allTeams[0].id).catch(() => null);
            if (!alive) return;
            setTeam(t);
          }
        }
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [activeToken.value, active.value?.targetId]);

  if (!activeToken.value) {
    return <EmptyAuth />;
  }

  if (loading) {
    return <div class="p-8 text-muted-foreground text-sm">Carregando…</div>;
  }

  if (!team) {
    return <div class="p-8 text-muted-foreground text-sm">Nenhum team disponível.</div>;
  }

  // Members = lista de agents.
  // O endpoint /teams/{id} pode retornar members embedded; senão usamos /agents.
  const members: AgentSummary[] = team.members && team.members.length > 0 ? team.members : agents;

  return (
    <div class="flex h-full">
      <TeamFlow
        team={team}
        members={members}
        selectedId={selected?.id ?? null}
        onSelect={(node) => setSelected(node)}
        onClose={() => setSelected(null)}
      />

      {selected && (
        <DetailPanel
          node={selected}
          isTeam={selected.id === team.id}
          onClose={() => setSelected(null)}
        />
      )}
    </div>
  );
}

function DetailPanel({
  node,
  isTeam,
  onClose,
}: {
  node: Node;
  isTeam: boolean;
  onClose: () => void;
}) {
  const tools: ToolSummary[] = isTeam
    ? normalizeTools((node as TeamSummary).tools)
    : normalizeTools((node as AgentSummary).tools);

  const instructions = !isTeam ? (node as AgentSummary).instructions : null;
  const promptText = Array.isArray(instructions)
    ? instructions.join('\n\n')
    : typeof instructions === 'string'
      ? instructions
      : '';

  return (
    <aside class="w-[28rem] border-l border-border bg-sidebar overflow-y-auto">
      <header class="h-14 px-4 border-b border-border flex items-center justify-between sticky top-0 bg-sidebar z-10">
        <div class="flex items-center gap-2">
          <i class={`ph ${isTeam ? 'ph-tree-structure' : 'ph-users'} text-base`} />
          <span class="font-medium text-sm">{node.name || node.id}</span>
        </div>
        <button
          class="inline-flex items-center justify-center size-8 rounded-md text-muted-foreground hover:bg-accent focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50"
          onClick={onClose}
        >
          <i class="ph ph-x text-base" />
        </button>
      </header>

      <div class="p-4 space-y-5">
        {'description' in node && node.description && (
          <Section label="Descrição">
            <p class="text-sm">{node.description}</p>
          </Section>
        )}

        <Section label="ID">
          <code class="font-mono text-xs">{node.id}</code>
        </Section>

        {(isTeam ? (node as TeamSummary).model : (node as AgentSummary).model) && (
          <Section label="Modelo">
            <pre class="text-[11px] font-mono bg-muted border border-border rounded-md p-2 overflow-auto">
{JSON.stringify(isTeam ? (node as TeamSummary).model : (node as AgentSummary).model, null, 2)}
            </pre>
          </Section>
        )}

        {promptText && (
          <Section label="Instructions">
            <CopyBlock content={promptText} />
          </Section>
        )}

        {tools.length > 0 && (
          <Section label={`Tools (${tools.length})`}>
            <div class="space-y-2">
              {tools.map((t) => (
                <div class="border border-border rounded-md p-2 bg-card">
                  <div class="text-sm font-mono">{t.name}</div>
                  {t.description && (
                    <p class="text-xs text-muted-foreground mt-1">{t.description}</p>
                  )}
                  {t.parameters?.properties && (
                    <details class="mt-1.5">
                      <summary class="text-[11px] text-muted-foreground cursor-pointer">
                        parâmetros
                      </summary>
                      <pre class="text-[11px] font-mono mt-1.5 bg-muted border border-border rounded-md p-2 overflow-auto">
{JSON.stringify(t.parameters.properties, null, 2)}
                      </pre>
                    </details>
                  )}
                </div>
              ))}
            </div>
          </Section>
        )}
      </div>
    </aside>
  );
}

function Section({ label, children }: { label: string; children: preact.ComponentChildren }) {
  return (
    <div>
      <div class="text-[11px] uppercase tracking-wider text-muted-foreground font-medium mb-1.5">{label}</div>
      {children}
    </div>
  );
}

function CopyBlock({ content }: { content: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <div class="relative">
      <pre class="text-xs whitespace-pre-wrap font-sans bg-muted border border-border rounded-md p-3 max-h-80 overflow-auto">
{content}
      </pre>
      <button
        class="absolute top-2 right-2 inline-flex items-center justify-center size-8 rounded-md border border-input bg-card shadow-xs hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50"
        onClick={() => {
          navigator.clipboard.writeText(content);
          setCopied(true);
          setTimeout(() => setCopied(false), 1500);
        }}
        title="Copiar"
      >
        {copied ? (
          <i class="ph ph-check text-sm text-success" />
        ) : (
          <i class="ph ph-copy text-sm" />
        )}
      </button>
    </div>
  );
}

function EmptyAuth() {
  return (
    <div class="p-12 text-center text-muted-foreground">
      <p class="text-sm">Configure o Bearer token na topbar para carregar a hierarquia.</p>
    </div>
  );
}
